import { Directive, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IComment, ICommentInfoGroup, Icons } from '@tecalisdev/infinity';
import { formatDeviceCreatedAtDisplay } from '../../../core/utils/utils';
import {
	deviceBrowserSummary,
	deviceDisplayName,
	devicesIpBlockUnlockMode,
	DevicesIpBlockUnlockMode,
	IProcessDeviceView,
	IProcessLocationView,
	ProcessDeviceBlockUnlockEvent,
} from '../../utils/process-devices.util';

/** @deprecated Usar `DevicesIpBlockUnlockMode` del util. */
export type ProcessDeviceBlockUnlock = DevicesIpBlockUnlockMode;

export type { ProcessDeviceBlockUnlockEvent };

export interface IProcessDeviceCardView {
	id: string;
	device: IProcessDeviceView;
	index: number;
	comment: IComment;
	blockUnlock: DevicesIpBlockUnlockMode;
	showEye: boolean;
}

/**
 * Lógica de dispositivos sobre el `tec-tile` del padre (sin host `app-*`).
 * UI NIF-983: `tec-comment` thread + `moreInfo.infoGroups` + `buttonIcons`
 * (Storybook Comments / with-rich-more-info, Infinity ≥ 19.81).
 */
@Directive({
	selector: '[appProcessDevicesTile]',
	standalone: true,
	exportAs: 'processDevicesTile',
})
export class ProcessDevicesTileDirective implements OnChanges {
	private readonly trans = inject(TranslateService);

	@Input() devices: IProcessDeviceView[] = [];
	@Input() locations: IProcessLocationView[] = [];
	/** IPs bloqueadas del proceso (`detail.locks.blocked_ips`). */
	@Input() blockedIps: { ip?: string }[] = [];
	@Input() canBlockIp = false;
	@Input() canUnblockIp = false;
	@Input() emptyTitleKey = 'GENERAL.NO_DATA';

	@Output() viewDeviceDetails = new EventEmitter<IProcessDeviceView>();
	@Output() blockUnlockClick = new EventEmitter<ProcessDeviceBlockUnlockEvent>();

	deviceCards: IProcessDeviceCardView[] = [];

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['devices'] || changes['blockedIps'] || changes['canBlockIp'] || changes['canUnblockIp']) {
			this.deviceCards = (this.devices ?? []).map((device, index) => this.mapDeviceCard(device, index));
		}
	}

	public onViewDetails(device: IProcessDeviceView): void {
		this.viewDeviceDetails.emit(device);
	}

	public onBlockUnlock(card: IProcessDeviceCardView): void {
		if (!card.blockUnlock) {
			return;
		}
		this.blockUnlockClick.emit({ action: card.blockUnlock, device: card.device });
	}

	/**
	 * Infinity aún no emite `clickEvent` de `buttonIcons` en moreInfo.
	 */
	public onDeviceCommentClick(event: MouseEvent, card: IProcessDeviceCardView): void {
		const target = event.target as HTMLElement | null;
		if (!target) {
			return;
		}
		if (card.showEye && this.clickMatchesIcon(target, 'icon-eye')) {
			event.preventDefault();
			event.stopPropagation();
			this.onViewDetails(card.device);
			return;
		}
		if (card.blockUnlock === 'block' && this.clickMatchesIcon(target, 'icon-lock')) {
			event.preventDefault();
			event.stopPropagation();
			this.onBlockUnlock(card);
			return;
		}
		if (card.blockUnlock === 'unlock' && this.clickMatchesIcon(target, 'icon-unlock')) {
			event.preventDefault();
			event.stopPropagation();
			this.onBlockUnlock(card);
		}
	}

	private clickMatchesIcon(target: HTMLElement, iconClass: string): boolean {
		return !!target.closest(`.${iconClass}, [class~="${iconClass}"]`);
	}

	private mapDeviceCard(device: IProcessDeviceView, index: number): IProcessDeviceCardView {
		const name = deviceDisplayName((key) => this.trans.instant(key), index);
		const dateLabel = formatDeviceCreatedAtDisplay(device.created_at);
		const browser = deviceBrowserSummary(device);
		const ip = device.ip?.trim() ? device.ip : '-';
		const blockUnlock = devicesIpBlockUnlockMode(
			device.ip,
			this.blockedIps,
			this.canBlockIp,
			this.canUnblockIp,
		);
		const showEye = !!device.user_agent;

		const moreInfoGroups: ICommentInfoGroup[] = [
			{
				bold: this.trans.instant('PROCESSES.DETAIL.BROWSER'),
				text: browser,
				...(showEye ? { buttonIcons: [{ icon: 'icon-eye' as Icons }] } : {}),
			},
			{
				bold: 'IP',
				text: ip,
				...(blockUnlock === 'block' ? { buttonIcons: [{ icon: 'icon-lock' as Icons }] } : {}),
				...(blockUnlock === 'unlock' ? { buttonIcons: [{ icon: 'icon-unlock' as Icons }] } : {}),
			},
		];

		const comment: IComment = {
			id: device.uuid || `device-${index}`,
			timestamp: 0,
			timestampLabel: dateLabel ?? undefined,
			infoGroup: {
				label: name,
			},
			moreInfo: {
				infoGroups: moreInfoGroups,
			},
		};

		return {
			id: comment.id,
			device,
			index,
			comment,
			blockUnlock,
			showEye,
		};
	}
}
